import type { PublishedTarifVersion } from '../api/group-tarifs';
import { parseErnMap } from '../../tarifs/model/manifest-form';
import { compareVersionNames } from '../../tarifs/model/version-list';

/**
 * The `refs` block of a group manifest, split into the two pins and everything else.
 *
 * A group version's refs are not free-form the way a single tarif's are: `childTarifVersion` and
 * `groupCore` are both required and both are pickers, because the service refuses a pin that is
 * not published, does not exist, or belongs to another tarif with a 409 (service summary §5.1).
 * Letting an operator type those by hand would mean the console cheerfully authoring manifests it
 * already knows will be refused.
 *
 * Everything else stays a text block. The manifest schema allows further refs beside the two, and
 * a picker cannot be built for refs whose candidates live in another service's catalog.
 */
export interface GroupRefs {
  readonly childTarifVersion: string;
  readonly groupCore: string;
  /** The group's PARTIAL billing override (SDD-015 B-7); empty means "inherit the child's rules whole". */
  readonly billingConfig: string;
  /**
   * The group's coverage override (SDD-018 §6); empty means "the child's Tarif & Deckung whole".
   * SDD-022 F-15 (found by the tutorial): without this pin no group version could carry it.
   */
  readonly tarifDeckung: string;
  /** The remaining `key = ern` lines, verbatim. */
  readonly other: string;
}

const PINNED = new Set(['childTarifVersion', 'groupCore', 'billingConfig', 'tarifDeckung']);

export function splitGroupRefs(refs: string): GroupRefs {
  const { entries } = parseErnMap(refs);
  const other = Object.keys(entries)
    .filter((key) => !PINNED.has(key))
    .sort((a, b) => a.localeCompare(b))
    .map((key) => `${key} = ${String(entries[key])}`)
    .join('\n');

  return {
    childTarifVersion: entries.childTarifVersion ?? '',
    groupCore: entries.groupCore ?? '',
    billingConfig: entries.billingConfig ?? '',
    tarifDeckung: entries.tarifDeckung ?? '',
    other,
  };
}

/**
 * Back to a refs block.
 *
 * An unset pin is OMITTED rather than written as `childTarifVersion = `. Both make the manifest
 * invalid, but they fail differently: the empty form is a malformed LINE — a typing mistake in
 * this editor — while the missing key is what the schema names, at
 * `refs.childTarifVersion`, which is the path the wizard turns back into a message under the
 * picker. Writing the empty line would produce two complaints about one hole, one of them
 * pointing at a mistake the operator did not make.
 */
export function joinGroupRefs({
  childTarifVersion,
  groupCore,
  billingConfig,
  tarifDeckung,
  other,
}: GroupRefs): string {
  const lines = [
    ...(childTarifVersion === '' ? [] : [`childTarifVersion = ${childTarifVersion}`]),
    ...(groupCore === '' ? [] : [`groupCore = ${groupCore}`]),
    ...(billingConfig === '' ? [] : [`billingConfig = ${billingConfig}`]),
    ...(tarifDeckung === '' ? [] : [`tarifDeckung = ${tarifDeckung}`]),
    ...other.split('\n').filter((line) => line.trim() !== ''),
  ];
  return lines.join('\n');
}

/**
 * Candidates, newest generation first and newest version first within it.
 *
 * A configurator pinning a child is almost always adopting the newest published version — that is
 * what a group PATCH exists for (service summary §3.2, the config-layer cascade) — so the order
 * puts it first instead of making them scroll a chronological list to the bottom.
 */
export function orderCandidates(
  candidates: readonly PublishedTarifVersion[],
): readonly PublishedTarifVersion[] {
  return [...candidates].sort((a, b) => {
    if (a.generation !== b.generation) return b.generation.localeCompare(a.generation);
    // `major.minor` compared as numbers (SDD-016 D-V10): 1.10 is newer than 1.9.
    return compareVersionNames(b.ern.slice(b.ern.lastIndexOf('/') + 1), a.ern.slice(a.ern.lastIndexOf('/') + 1));
  });
}
